import { EventBus } from './utils/event-bus';
import { TokenService } from '../TokenService';
import { PdfViewerPage } from './components/pdf-viewer-page/pdf-viewer-page.component';
import { PdfViewerPageMapper } from './helpers/pdf-viewer-page-mapper';
import { PdfThumbnailViewer } from './components/pdf-thumbnail-viewer/pdf-thumbnail-viewer.component';
import { PdfViewerEventType, PdfViewerNavigateToPageEvent, PdfViewerNavigateToPageNumberEvent, PdfViewerThumbnailsOpenedChangeEvent } from './+types/pdf-viewer-event.type';
import { PdfViewerLayoutType } from './+types/pdf-viewer-layout.type';
import { PdfViewerLayoutOneColumn, PdfViewerLayoutSinglePage } from './layout';
import { PdfJsService } from './services/pdfjs.service';
import { PdfViewerToolbarComponent } from './components/pdf-viewer-toolbar/pdf-viewer-toolbar.component';
import { FullscreenHelper, FullscreenHelperEventType } from './helpers/fullscreen.helper';
import { SearchService } from './services/search.service';
import { PdfSearchComponent } from './components/pdf-search/pdf-search.component';
import { PdfViewerPageHighlightService } from './services/pdf-viewer-page-highlight.service';
import { PendoService } from './services/pendo.service';
import { PdfSearchButtonComponent } from './components/pdf-search-button/pdf-search-button.component';
export class PdfViewer {
    constructor(config) {
        this.config = config;
        this.pages = [];
        this.pagesMappedById = new Map();
        this.pagesByPageNumber = new Map();
        this.resize = () => {
            if (this.viewerLayout) {
                this.viewerLayout.resize();
            }
            if (this.thumbnailViewer) {
                this.thumbnailViewer.resize();
            }
        };
        this.onSwitchLayout = (event) => {
            if (event && event.payload.layout) {
                this.setLayout(event.payload.layout);
            }
        };
        this.onToggleThumbnails = (event) => {
            this.eventBus.dispatch(new PdfViewerThumbnailsOpenedChangeEvent({ opened: !this.thumbnailsVisible }));
        };
        this.onThumbnailsOpenedChange = (event) => {
            const { opened } = event.payload;
            this.thumbnailsVisible = opened;
            if (this.thumbnailViewer) {
                if (opened) {
                    return this.thumbnailViewer.on(this.pageIdFocused);
                }
                this.thumbnailViewer.off();
            }
        };
        this.onFetchSearchResults = (event) => {
            const { pages } = event.payload;
            if (pages.length) {
                if (window.innerWidth > 600) {
                    this.eventBus.dispatch(new PdfViewerThumbnailsOpenedChangeEvent({ opened: true }));
                }
                this.eventBus.dispatch(new PdfViewerNavigateToPageNumberEvent({ pageNumber: pages[0] }));
            }
        };
        this.initializePendo = () => {
            if (this.config.pendoConfig) {
                this.pendoService = new PendoService(this.config.pendoConfig.pendoTrackFunction, this.config.pendoConfig.defaultTrackEventMetadata);
            }
        };
        this.initializeSearch = () => {
            if (this.config.searchConfig) {
                this.searchService = new SearchService(this.config.searchConfig.id, this.config.searchConfig.searchAvailable, this.config.searchConfig.onSearch, Array.from(this.config.pages.keys()), this.eventBus, this.pendoService);
                this.searchButtonComponent = new PdfSearchButtonComponent(this.config.searchConfig.container, this.eventBus, this.config.translations);
                this.searchComponentWrapper = document.createElement('div');
                this.searchComponentWrapper.classList.add('sp-pdf-search-wrapper');
                this.container.appendChild(this.searchComponentWrapper);
                this.toggleFullscreenModelClassInSearch();
                this.searchComponent = new PdfSearchComponent(this.pageNumberFocused, this.searchComponentWrapper, this.eventBus, this.searchService, this.config.translations, this.pendoService);
                this.pdfViewerPageHighlightService = new PdfViewerPageHighlightService(this.eventBus, this.searchService, this.pagesByPageNumber);
            }
        };
        this.destroySearch = () => {
            if (this.searchService) {
                this.searchService.destroy();
                this.searchService = null;
            }
            if (this.searchComponent) {
                this.searchComponent.destroy();
                this.searchComponent = null;
            }
            if (this.searchButtonComponent) {
                this.searchButtonComponent.destroy();
                this.searchButtonComponent = null;
            }
            if (this.pdfViewerPageHighlightService) {
                this.pdfViewerPageHighlightService.destroy();
                this.pdfViewerPageHighlightService = null;
            }
        };
        this.onNavigateToPage = (event) => {
            this.viewerLayout.goto(event.payload.pageId);
        };
        // Proxy till we refactor go to page....
        this.onNavigateToPageNumber = (event) => {
            const { pageNumber } = event.payload;
            const pageId = this.config.pages.get(pageNumber).id;
            if (pageId) {
                this.eventBus.dispatch(new PdfViewerNavigateToPageEvent({ pageId }));
            }
        };
        this.onResize = () => {
            this.resize();
        };
        this.onZoomIn = (event) => {
            this.viewerLayout.zoomIn();
        };
        this.onZoomOut = (event) => {
            this.viewerLayout.zoomOut();
        };
        this.onFullscreenChange = (event) => {
            this.updateToolbar();
            this.toggleFullscreenModelClassInSearch();
        };
        this.eventBus = new EventBus();
        this.fullscreenHelper = new FullscreenHelper();
        this.tokenService = new TokenService({ refreshToken: this.config.refreshToken });
        this.pdfJsService = new PdfJsService(config.getPdfJs(), config.url, config.pages, config.fileSize, config.defaultRangeChunkSize, config.minRangeChunkSize, config.maxRangeChunkSize, this.eventBus);
        if (this.config.thumbnailContainer) {
            const pdfThumbnailViewerConfig = this.getPdfThumbnailViewerConfig(this.config);
            this.thumbnailViewer = new PdfThumbnailViewer(this.eventBus, pdfThumbnailViewerConfig, this.pdfJsService);
        }
        window['PDF_VIEWER'] = this;
        this.createPdfViewerPages();
    }
    get pageIdFocused() {
        return this.viewerLayout && this.viewerLayout.pageIdFocused ? this.viewerLayout.pageIdFocused : this.initialPageId;
    }
    get pageNumberFocused() {
        // TODO refactor this when we move from pageId to pageNumber
        return parseInt(this.pageIdFocused.split('-')[1]);
    }
    init(container = null, initialPageId, thumbnailsVisible = true, isPageSelectionEnabled = false) {
        if (container) {
            this.container = container;
        }
        this.initialPageId = initialPageId || Array.from(this.pages)[0].id;
        this.thumbnailsVisible = thumbnailsVisible;
        this.isPageSelectionEnabled = isPageSelectionEnabled;
        this.container.innerHTML = '';
        this.layoutElement = document.createElement('div');
        this.layoutElement.classList.add('sp-pdf-viewer-layout');
        this.container.appendChild(this.layoutElement);
        this.initializeToolbar();
        this.setLayout(this.config.layout);
        if (this.thumbnailViewer && thumbnailsVisible) {
            this.thumbnailViewer.on(this.pageIdFocused);
        }
        if (this.thumbnailViewer && isPageSelectionEnabled) {
            this.thumbnailViewer.on(this.pageIdFocused);
            this.enablePageSelection();
        }
        this.removeEventListeners();
        this.addEventListeners();
        this.initializePendo();
        this.initializeSearch();
    }
    resetAnnotations() {
        this.pages.forEach((page) => page.resetAnnotations());
    }
    next() {
        this.viewerLayout.next();
    }
    previous() {
        this.viewerLayout.previous();
    }
    goto(pageId) {
        if (this.viewerLayout) {
            this.viewerLayout.goto(pageId);
        }
    }
    getPages() {
        return this.pages.map((page) => PdfViewerPageMapper.mapToPublicPdfViewerPage(page));
    }
    canNavigateToNext() {
        return this.viewerLayout.canNavigateToNext();
    }
    canNavigateToPrevious() {
        return this.viewerLayout.canNavigateToPrevious();
    }
    enablePageSelection() {
        this.isPageSelectionEnabled = true;
        if (this.thumbnailViewer) {
            this.thumbnailViewer.enablePageSelection();
        }
        this.updateToolbar();
    }
    disablePageSelection() {
        this.isPageSelectionEnabled = false;
        if (this.thumbnailViewer) {
            this.thumbnailViewer.disablePageSelection();
        }
        this.updateToolbar();
    }
    destroy() {
        this.removeEventListeners();
        this.destroySearch();
        if (this.pdfJsService) {
            this.pdfJsService.destroy();
        }
        if (this.viewerLayout) {
            this.viewerLayout.destroy();
        }
        if (this.thumbnailViewer) {
            this.thumbnailViewer.destroy();
        }
        if (this.toolbar) {
            this.toolbar.destroy();
        }
    }
    addEventListeners() {
        this.eventBus.on(PdfViewerEventType.NavigateToPage, this.onNavigateToPage);
        this.eventBus.on(PdfViewerEventType.NavigateToPageNumber, this.onNavigateToPageNumber);
        this.eventBus.on(PdfViewerEventType.ZoomIn, this.onZoomIn);
        this.eventBus.on(PdfViewerEventType.ZoomOut, this.onZoomOut);
        this.eventBus.on(PdfViewerEventType.SwitchLayout, this.onSwitchLayout);
        this.eventBus.on(PdfViewerEventType.ThumbnailsOpenedChange, this.onThumbnailsOpenedChange);
        this.eventBus.on(PdfViewerEventType.ToggleThumbnails, this.onToggleThumbnails);
        this.eventBus.on(PdfViewerEventType.FetchSearchResults, this.onFetchSearchResults);
        this.fullscreenHelper.on(FullscreenHelperEventType.Change, this.onFullscreenChange);
        window.addEventListener('resize', this.onResize);
    }
    removeEventListeners() {
        this.eventBus.off(PdfViewerEventType.NavigateToPage, this.onNavigateToPage);
        this.eventBus.off(PdfViewerEventType.NavigateToPageNumber, this.onNavigateToPageNumber);
        this.eventBus.off(PdfViewerEventType.ZoomIn, this.onZoomIn);
        this.eventBus.off(PdfViewerEventType.ZoomOut, this.onZoomOut);
        this.eventBus.off(PdfViewerEventType.SwitchLayout, this.onSwitchLayout);
        this.eventBus.off(PdfViewerEventType.ThumbnailsOpenedChange, this.onThumbnailsOpenedChange);
        this.eventBus.off(PdfViewerEventType.ToggleThumbnails, this.onToggleThumbnails);
        this.fullscreenHelper.off(FullscreenHelperEventType.Change, this.onFullscreenChange);
        window.removeEventListener('resize', this.onResize);
    }
    setLayout(layout) {
        const pageIdFocused = this.pageIdFocused;
        if (this.viewerLayout) {
            this.viewerLayout.destroy();
        }
        switch (layout) {
            case PdfViewerLayoutType.OneColumn:
                this.viewerLayout = new PdfViewerLayoutOneColumn(this.eventBus, this.config);
                break;
            case PdfViewerLayoutType.SinglePage:
            default:
                this.viewerLayout = new PdfViewerLayoutSinglePage(this.eventBus, this.config);
                break;
        }
        this.viewerLayout.init(this.layoutElement, this.pages, pageIdFocused);
    }
    initializeToolbar() {
        if (!this.config.toolbarConfig) {
            return;
        }
        this.toolbar = new PdfViewerToolbarComponent(this.container, this.eventBus, this.config.toolbarConfig, this.config.translations);
        this.toolbar.render();
    }
    getPdfThumbnailViewerConfig(config) {
        const { id, thumbnailContainer, name, url, pages, tintColor, getPdfJs, getPdfJsWorker, lockedPages, selectedPageIds } = config;
        return {
            id,
            thumbnailContainer,
            name,
            url,
            pages,
            tintColor,
            getPdfJs,
            getPdfJsWorker,
            lockedPages,
            selectedPageIds
        };
    }
    toggleFullscreenModelClassInSearch() {
        if (!this.searchComponentWrapper) {
            return;
        }
        if (this.fullscreenHelper.isFullscreen) {
            this.searchComponentWrapper.classList.add('sp-fullscreen-mode');
        }
        else {
            this.searchComponentWrapper.classList.remove('sp-fullscreen-mode');
        }
    }
    updateToolbar() {
        if (!this.toolbar) {
            return;
        }
        if (this.isPageSelectionEnabled || this.fullscreenHelper.isFullscreen) {
            return this.toolbar.disableToggleThumbnails();
        }
        else {
            this.toolbar.enableToggleThumbnails();
        }
    }
    createPdfViewerPages() {
        this.config.pages.forEach((pageConfig) => {
            const page = new PdfViewerPage(this.config, pageConfig, this.eventBus, this.pdfJsService, this.tokenService);
            this.pages.push(page);
            this.pagesMappedById.set(page.id, page);
            this.pagesByPageNumber.set(page.pageNumber, page);
        });
    }
}
//# sourceMappingURL=pdf-viewer.js.map